import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "../../../contexts/InfoContext";
import { useAuth } from "../../../contexts/AuthContext";
import getRequiredUserData from "../../../utils/getBranchInfo";
import createCustomer from "../../../api/customer/create";

const AddCustomerForm = ({ open, onClose, branchId }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { openSnackbar } = useSnackbar();
  const userData = getRequiredUserData();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    blockHouse: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    if (!formData.blockHouse.trim())
      newErrors.blockHouse = "Block/House is required";
    return newErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const customerData = {
        name: formData.name,
        phone: formData.phone,
        blockHouse: formData.blockHouse.toUpperCase(),
        branchKey: branchId,
        branchName: userData.branchName || "",
      };

      await createCustomer(user, customerData);

      openSnackbar("Customer created successfully!", "success");
      setFormData({ name: "", phone: "", blockHouse: "" });
      onClose();
    } catch (error) {
      openSnackbar(
        error?.response?.data?.message || "Failed to create customer.",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: theme.palette.background.alt }}>
        Add New Customer
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.palette.background.alt }}>
        <Box
          component="form"
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
        >
          <TextField
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            fullWidth
            error={!!errors.name}
            helperText={errors.name}
            disabled={isSubmitting}
            InputLabelProps={{
              style: { color: theme.palette.secondary[100] },
            }}
          />
          <TextField
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            fullWidth
            error={!!errors.phone}
            helperText={errors.phone}
            disabled={isSubmitting}
            InputLabelProps={{
              style: { color: theme.palette.secondary[100] },
            }}
          />
          <TextField
            label="Block/House"
            name="blockHouse"
            value={formData.blockHouse}
            onChange={handleChange}
            required
            fullWidth
            error={!!errors.blockHouse}
            helperText={errors.blockHouse}
            disabled={isSubmitting}
            InputLabelProps={{
              style: { color: theme.palette.secondary[100] },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: theme.palette.background.alt }}>
        <Button
          onClick={onClose}
          color="secondary"
          disabled={isSubmitting}
          sx={{
            color: theme.palette.secondary[100],
            "&:hover": {
              backgroundColor: theme.palette.background.alt,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          sx={{
            color: theme.palette.secondary[100],
            "&:hover": {
              backgroundColor: theme.palette.background.alt,
            },
          }}
        >
          {isSubmitting ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCustomerForm;
